import {routes} from '@/lib/routes'
import type {Instant, UUID} from './common'

// ============================================================================
// Form Layout Schema — JSONB types
// ============================================================================

/**
 * A field sourced from a core entity field (e.g. displayName, externalId).
 */
export interface EntityLayoutField {
    source: 'entity'
    fieldKey: string
    label?: string
    placeholder?: string
    defaultValue?: string
    templateExpression?: string
    colSpan?: number
    disabled?: boolean
    hidden?: boolean
    visibilityCondition?: VisibilityCondition
}

/**
 * A field sourced from an attribute definition.
 */
export interface AttributeLayoutField {
    source: 'attribute'
    definitionId: UUID
    label?: string
    placeholder?: string
    defaultValue?: string
    templateExpression?: string
    colSpan?: number
    requiredOverride?: 'inherit' | 'required' | 'optional'
    disabled?: boolean
    hidden?: boolean
    visibilityCondition?: VisibilityCondition
}

/**
 * A field sourced from a relationship type (party-relationship use-case only).
 */
export interface RelationshipLayoutField {
    source: 'relationship'
    relationshipType: string
    label?: string
    placeholder?: string
    defaultValue?: string
    templateExpression?: string
    colSpan?: number
    disabled?: boolean
    hidden?: boolean
    visibilityCondition?: VisibilityCondition
}

/**
 * Conditional visibility rule for a field.
 */
export interface VisibilityCondition {
    fieldKey: string
    equalTo: string
}

/**
 * A layout field is a union of the three field source types.
 */
export type LayoutField = EntityLayoutField | AttributeLayoutField | RelationshipLayoutField

/**
 * A section within the layout, grouping fields together.
 */
export interface LayoutSection {
    key: string
    label: string
    collapsible: boolean
    columns: 1 | 2 | 3
    fields: LayoutField[]
}

/**
 * The full layout schema stored as JSONB.
 */
export interface LayoutSchema {
    schemaVersion: number
    gridColumns: number
    sections: LayoutSection[]
}

// ============================================================================
// Form Layout
// ============================================================================

/**
 * Full form layout response (matches FormLayoutResponse).
 * In render mode, layoutSchema has hidden fields stripped.
 */
export interface FormLayout {
    formLayoutId: UUID
    useCaseKey: string
    displayName: string
    schemaVersion: number
    layoutSchema: LayoutSchema
    active: boolean
    version: number
    createdAt: Instant
    updatedAt: Instant
    createdBy: UUID | null
    updatedBy: UUID | null
}

/**
 * List projection for a form layout (matches FormLayoutListItemResponse).
 * Omits the full schema for efficiency.
 */
export interface FormLayoutListItem {
    formLayoutId: UUID
    useCaseKey: string
    displayName: string
    active: boolean
    fieldCount: number
    totalFieldCount: number
    updatedAt: Instant
    updatedByDisplayName: string | null
}

// ============================================================================
// Palette
// ============================================================================

/**
 * A core entity field item in the palette.
 */
export interface PaletteEntityField {
    fieldKey: string
    label: string
    required: boolean
}

/**
 * An attribute definition item in the palette.
 */
export interface PaletteAttributeDefinition {
    id: UUID
    name: string
    displayName: string
    valueType: string
    required: boolean
    active: boolean
    iamManaged: boolean
}

/**
 * A relationship schema entry in the palette (party-relationship use-case only).
 */
export interface PaletteRelationshipField {
    relationshipType: string
    fromSideLabel: string | null
    toPartyType: string
    sortOrder: number
}

/**
 * Palette response for the form layout designer (matches PaletteResponse).
 */
export interface Palette {
    entityFields: PaletteEntityField[]
    attributeDefinitions: PaletteAttributeDefinition[]
    readOnlyFields: PaletteAttributeDefinition[]
    relationshipFields: PaletteRelationshipField[]
}

// ============================================================================
// Upsert request
// ============================================================================

export interface UpsertFormLayout {
    displayName: string
    layoutSchema: LayoutSchema
    active: boolean
    version?: number | null
}

export interface UpsertFormLayoutRequest {
    displayName: string
    layoutSchema: Record<string, unknown>
    active: boolean
    version?: number
}

// ============================================================================
// Designer state / actions
// ============================================================================

export type DesignerAction =
    | {type: 'ADD_SECTION'}
    | {type: 'REMOVE_SECTION'; sectionKey: string}
    | {type: 'REORDER_SECTIONS'; fromIndex: number; toIndex: number}
    | {type: 'ADD_FIELD'; sectionKey: string; field: LayoutField}
    | {type: 'REMOVE_FIELD'; sectionKey: string; fieldIndex: number}
    | {type: 'REORDER_FIELDS'; sectionKey: string; fromIndex: number; toIndex: number}
    | {
          type: 'MOVE_FIELD'
          fromSectionKey: string
          toSectionKey: string
          fieldIndex: number
          toIndex: number
      }
    | {type: 'UPDATE_FIELD'; sectionKey: string; fieldIndex: number; patch: Partial<LayoutField>}
    | {type: 'UPDATE_SECTION'; sectionKey: string; patch: Partial<Omit<LayoutSection, 'key' | 'fields'>>}
    | {type: 'RESET'; schema: LayoutSchema}
    | {type: 'LAYOUT_REORDER_OPTIMISTIC'; nextSchema: LayoutSchema}
    | {type: 'LAYOUT_REORDER_REVERT'; baseline: LayoutSchema}

/**
 * Selection in the designer (field or section).
 */
export type DesignerSelection =
    | {type: 'field'; sectionKey: string; fieldIndex: number}
    | {type: 'section'; sectionKey: string}
    | null

// ============================================================================
// Use-case registry
// ============================================================================

export interface UseCaseDefinition {
    useCaseKey: string
    displayName: string
    /** i18n key suffix for the wizard step hint */
    wizardStepKey: string
    /** Direct link to the page where this layout is used */
    usedOnPath: string
}

/**
 * Static Tailwind class map for CSS grid column spans.
 * IMPORTANT: Do NOT use dynamic `col-span-${n}` — Tailwind JIT cannot detect dynamic classes.
 */
export const COL_SPAN_CLASS: Record<number, string> = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12',
}

/**
 * Resolve a human-readable display label for any LayoutField using palette data.
 * Single source of truth — used by FieldCard, PreviewPane, drag overlay, and toast messages.
 */
export function resolveFieldLabel(field: LayoutField, palette: Palette | null): string {
    if (field.label) return field.label
    if (field.source === 'entity') {
        if (palette) {
            const ef = palette.entityFields.find((e) => e.fieldKey === field.fieldKey)
            if (ef) return ef.label
        }
        return field.fieldKey
    }
    if (field.source === 'attribute') {
        if (palette) {
            const def =
                palette.attributeDefinitions.find((d) => d.id === field.definitionId) ??
                palette.readOnlyFields.find((d) => d.id === field.definitionId)
            if (def) return def.displayName
        }
        return field.definitionId
    }
    if (field.source === 'relationship') {
        if (palette) {
            const rel = palette.relationshipFields.find((r) => r.relationshipType === field.relationshipType)
            if (rel?.fromSideLabel) return rel.fromSideLabel
        }
        return field.relationshipType
    }
    return ''
}

export const USE_CASE_REGISTRY: UseCaseDefinition[] = [
    {useCaseKey: 'party-entity', displayName: 'Person Entity Fields', wizardStepKey: 'wizardStepPerson', usedOnPath: routes.identityManagement.onboarding.create()},
    {useCaseKey: 'party-relationship', displayName: 'Person Relationships', wizardStepKey: 'wizardStepOrgRelationships', usedOnPath: routes.identityManagement.onboarding.create()},
    {useCaseKey: 'party-attribute', displayName: 'Party Attributes', wizardStepKey: 'wizardStepOrgAttributes', usedOnPath: routes.identityManagement.onboarding.create()},
    {useCaseKey: 'identity-entity', displayName: 'Identity Entity Fields', wizardStepKey: 'wizardStepIdentity', usedOnPath: routes.identityManagement.onboarding.create()},
    {useCaseKey: 'identity-attribute', displayName: 'Identity Attributes', wizardStepKey: 'wizardStepIdentityAttributes', usedOnPath: routes.identityManagement.onboarding.create()},
]
